import type { Person, CreateContractDto } from '../types/contract.types';
import { isValidPhone as isValidPhoneIntl } from '@/utils/phone';

/**
 * Valide un email
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valide un numéro de téléphone international (E.164).
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone) return false;
  return isValidPhoneIntl(phone);
};

/**
 * Valide un code postal français
 */
export const isValidPostalCode = (cp: string): boolean => {
  const cpRegex = /^[0-9]{5}$/;
  return cpRegex.test(cp);
};

/** Borne max plausible pour une date de contrat (année courante + 5). */
export const maxContractYear = (): number => new Date().getFullYear() + 5;
/** Borne max plausible pour une date véhicule (année courante + 1). */
export const maxVehicleYear = (): number => new Date().getFullYear() + 1;

/**
 * Valide qu'une date ISO (YYYY-MM-DD) a une année dans [minYear, maxYear].
 * Attrape l'aberration « année à 2 chiffres » (ex : « 26 » → « 0026 ») que le
 * <input type="date"> peut produire (ISO valide mais année absurde).
 */
const isYearInRange = (dateStr: string, minYear: number, maxYear: number): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return false;
  if (Number.isNaN(new Date(dateStr).getTime())) return false;
  const year = Number(match[1]);
  return year >= minYear && year <= maxYear;
};

/** Date de contrat (début/fin) : année dans [2020, année courante + 5]. */
export const isPlausibleContractDate = (dateStr: string): boolean =>
  isYearInRange(dateStr, 2020, maxContractYear());

/** Date véhicule (mise en circulation / achat) : année dans [1900, année courante + 1]. */
export const isPlausibleVehicleDate = (dateStr: string): boolean =>
  isYearInRange(dateStr, 1900, maxVehicleYear());

/**
 * Valide les informations d'une personne
 */
export const validatePerson = (person: Person, label: string): string[] => {
  const errors: string[] = [];

  if (!person.firstName?.trim()) {
    errors.push(`${label}: Le prénom est requis`);
  }

  if (!person.lastName?.trim()) {
    errors.push(`${label}: Le nom est requis`);
  }

  if (!person.email?.trim()) {
    errors.push(`${label}: L'email est requis`);
  } else if (!isValidEmail(person.email)) {
    errors.push(`${label}: L'email est invalide`);
  }

  if (person.phoneNumber && !isValidPhone(person.phoneNumber)) {
    errors.push(`${label}: Le téléphone est invalide`);
  }

  if (person.cp && !isValidPostalCode(person.cp)) {
    errors.push(`${label}: Le code postal est invalide`);
  }

  return errors;
};

/**
 * Valide un DTO de création de contrat
 */
export const validateCreateContract = (dto: CreateContractDto): string[] => {
  const errors: string[] = [];

  if (!dto.warrantyUuid) {
    errors.push('La garantie est requise');
  }

  if (!dto.externalId?.trim()) {
    errors.push('Le numéro de commande est requis');
  }

  if (!dto.subscriptionDate) {
    errors.push('La date de souscription est requise');
  } else if (!isPlausibleContractDate(dto.subscriptionDate)) {
    errors.push(
      `La date de prise d'effet est invalide (année attendue entre 2020 et ${maxContractYear()})`,
    );
  }

  if (dto.extensionEndDate && !isPlausibleContractDate(dto.extensionEndDate)) {
    errors.push(
      `La date de fin du contrat est invalide (année attendue entre 2020 et ${maxContractYear()})`,
    );
  }

  errors.push(...validatePerson(dto.subscriber, 'Souscripteur'));
  errors.push(...validatePerson(dto.beneficiary, 'Bénéficiaire'));

  if (!dto.sellingPrice || dto.sellingPrice <= 0) {
    errors.push('Le prix de vente doit être supérieur à 0');
  }

  if (dto.quantity !== undefined && dto.quantity < 1) {
    errors.push('La quantité doit être au moins 1');
  }

  return errors;
};
